/* ==========================
    IMPORTS & CONFIGURATION
   ========================== */
import React, { useState, useEffect, useRef } from "react";
import { db, storage } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { collection, getDocs, doc, updateDoc, deleteDoc, addDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Trash2, Edit, X, Download } from "lucide-react";

export default function InstructorPortal({ activeTab, initialSubTab, setInitialSubTab }) {
  const { userData } = useAuth();
  
  /* =======================
      STATE CONFIGURATIONS
     ======================= */
  
  /* Main Database Collections */
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [pastAttendance, setPastAttendance] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [announcementsList, setAnnouncementsList] = useState([]);
  
  /* Active Sub-tab Selector */
  const [subTab, setSubTab] = useState("");

  /* Mapped Instructor Assignments */
  const [assignedCourses, setAssignedCourses] = useState([]);
  const [assignedBatches, setAssignedBatches] = useState([]);

  /* Upload Materials States */
  const [targetCourse, setTargetCourse] = useState("");
  const [targetBatch, setTargetBatch] = useState("");
  const [materialTitle, setMaterialTitle] = useState("");
  const [materialLink, setMaterialLink] = useState("");
  const [youtubeLink, setYoutubeLink] = useState("");
  const [fileValidationMessage, setFileValidationMessage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [editingMaterialId, setEditingMaterialId] = useState(null);

  /* Attendance Records States */
  const [attendanceCourse, setAttendanceCourse] = useState(""); 
  const [attendanceBatch, setAttendanceBatch] = useState("");   
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split("T")[0]);
  const [attendanceRecords, setAttendanceRecords] = useState({});
  const [editingAttendanceId, setEditingAttendanceId] = useState(null);

  /* Exam Builder States */
  const [assessmentCourse, setAssessmentCourse] = useState("");
  const [assessmentBatch, setAssessmentBatch] = useState("");
  const [assessmentTitle, setAssessmentTitle] = useState("");
  const [assessmentDeadline, setAssessmentDeadline] = useState("");
  const [assessmentType, setAssessmentType] = useState("mcq"); 
  const [gradingType, setGradingType] = useState("auto"); 
  const [questions, setQuestions] = useState([{ questionText: "", options: ["", "", "", ""], correctOptionNum: "1", marks: "" }]);
  
  /* Deploy Warning Modal States */
  const [showDeploymentModal, setShowDeploymentModal] = useState(false);
  const [totalAssessmentMarks, setTotalAssessmentMarks] = useState(0);

  /* Grading & Scores States */
  const [selectedSubmissionId, setSelectedSubmissionId] = useState(null);
  const [gradeScore, setGradeScore] = useState("");
  const [gradeFeedback, setGradeFeedback] = useState("");
  const [manualGradeAnswers, setManualGradeAnswers] = useState({}); 
  const [structuredOverrideScores, setStructuredOverrideScores] = useState({}); 

  /* Class Announcements States */
  const [announceCourse, setAnnounceCourse] = useState("");
  const [announceBatch, setAnnounceBatch] = useState("");
  const [announceTitle, setAnnounceTitle] = useState("");
  const [announceContent, setAnnounceContent] = useState("");
  const [editingAnnounceId, setEditingAnnounceId] = useState(null);

  /* Student Course Requests */
  const [mapStudentId, setMapStudentId] = useState("");
  const [mapCourseCode, setMapCourseCode] = useState("");
  const [mapBatchName, setMapBatchName] = useState("");

  /* Video Embed Media players */
  const [activeVideoUrl, setActiveVideoUrl] = useState("");
  const [activeVideoType, setActiveVideoType] = useState(""); 

  /* Warning Alerts & Popups */
  const [customAlert, setCustomAlert] = useState(null); 
  const alertModalRef = useRef(null);
  const deploymentModalRef = useRef(null);

  /* ============================
      DATABASE LIFECYCLE & SYNC
     ============================ */
  
  // This hook keeps all our list tables updated and sets default sub-tabs dynamically
  useEffect(() => {
    fetchInstructorDataset();
    if (activeTab === "dashboard") {
      setSubTab("overview");
    } else if (activeTab === "materials") {
      setSubTab("upload-materials");
    } else if (activeTab === "attendance") {
      setSubTab("mark-attendance");
    } else if (activeTab === "assessments") {
      setSubTab("create-assessment");
    } else if (activeTab === "grading") {
      setSubTab("new-grading");
    } else if (activeTab === "announcements") {
      if (initialSubTab) {
        setSubTab(initialSubTab);
        setTimeout(() => {
          if (setInitialSubTab) setInitialSubTab("");
        }, 50);
      } else {
        if (subTab !== "broadcast" && subTab !== "sent-history" && subTab !== "received") {
          setSubTab("broadcast");
        }
      }
    } else {
      setSubTab("");
    }
  }, [activeTab, initialSubTab]);

  // Filters the classes list to display only those assigned to this instructor profile
  useEffect(() => {
    if (userData) {
      const allowedCourses = userData.assignedCourses || []; 
      const allowedBatches = userData.assignedBatches || []; 
      setAssignedCourses(courses.filter(c => allowedCourses.includes(c.code)));
      setAssignedBatches(batches.filter(b => allowedBatches.includes(b.batchName)));
    }
  }, [courses, batches, userData]);

  // Clears up the questions array to start fresh when switching exam formats
  useEffect(() => {
    resetQuestions();
  }, [assessmentType, gradingType]);

  // Listener to hide warning dialogs when clicking elsewhere on the page
  useEffect(() => {
    function handleClickOutside(event) {
      if (alertModalRef.current && !alertModalRef.current.contains(event.target)) {
        setCustomAlert(null);
      }
      if (deploymentModalRef.current && !deploymentModalRef.current.contains(event.target)) {
        setShowDeploymentModal(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function resetQuestions() {
    if (assessmentType === "mcq") {
      setQuestions([{ questionText: "", options: ["", "", "", ""], correctOptionNum: "1", marks: "" }]);
    } else {
      setQuestions([{ questionText: "", marks: "" }]);
    }
  }

  /* ================================
      CORE DATABASE CONTEXT FETCHERS
     ================================ */
  async function fetchInstructorDataset() {
    try {
      const cSnap = await getDocs(collection(db, "courses"));
      setCourses(cSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(c => c.status === "active"));

      const bSnap = await getDocs(collection(db, "batches"));
      setBatches(bSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const uSnap = await getDocs(collection(db, "users"));
      setStudents(uSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(u => u.role === "student" && u.status === "approved"));

      const mSnap = await getDocs(collection(db, "materials"));
      setMaterials(mSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const asSnap = await getDocs(collection(db, "assessments"));
      setAssessments(asSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const annSnap = await getDocs(collection(db, "announcements"));
      setAnnouncementsList(annSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const aSnap = await getDocs(collection(db, "attendance"));
      setPastAttendance(aSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      if (activeTab === "grading") {
        const sSnap = await getDocs(collection(db, "submissions"));
        setSubmissions(sSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      }
    } catch (err) {
      console.error("Failed to load backend lists: ", err);
    }
  }

  /* =================================
      RESOURCE FILE FORMAT VALIDATORS
     ================================= */
  async function handleRealStorageUpload(e) {
    const file = e.target.files[0];
    if (!file) return;

    const allowedExtensions = /(\.pdf|\.pptx|\.docx|\.zip|\.txt|\.html|\.css|\.js|\.py|\.php|\.java|\.c|\.cpp|\.json|\.xml|\.mp4)$/i;
    if (!allowedExtensions.exec(file.name)) {
      setFileValidationMessage("⚠️ This file format not allowed!");
      return;
    }

    if (file.size > 500 * 1024 * 1024) {
      setFileValidationMessage("⚠️ File size exceeds 500MB limit.");
      return;
    }

    setFileValidationMessage("");
    setUploading(true);
    try {
      const fileRef = ref(storage, `materials/${Date.now()}-${file.name}`);
      await uploadBytes(fileRef, file);
      const downloadUrl = await getDownloadURL(fileRef);
      setMaterialLink(downloadUrl);
      setCustomAlert({ type: "success", message: "File uploaded" });
    } catch (err) {
      setCustomAlert({ type: "error", message: "Upload failed: " + err.message });
    }
    setUploading(false);
  }

  /* ==================
      ACTION HANDLERS
     ================== */

  /* Upload Course Lessons & Notes */
  async function handleMaterialUpload(e) {
    e.preventDefault();
    if (!targetCourse || !materialTitle) return;

    if (editingMaterialId) {
      await updateDoc(doc(db, "materials", editingMaterialId), {
        title: materialTitle,
        link: materialLink || "",
        youtubeLink: youtubeLink || "",
        courseId: targetCourse,
        batchId: targetBatch || "all"
      });
      setCustomAlert({ type: "success", message: "Course material updated successfully!" });
      setEditingMaterialId(null);
    } else {
      if (!materialLink && !youtubeLink) {
        setCustomAlert({ type: "error", message: "Please upload File or YouTube link." });
        return;
      }
      await addDoc(collection(db, "materials"), {
        title: materialTitle,
        link: materialLink || "",
        youtubeLink: youtubeLink || "",
        courseId: targetCourse,
        batchId: targetBatch || "all",
        uploadedAt: new Date().toISOString()
      });
      setCustomAlert({ type: "success", message: "Course material published successfully!" });
    }

    setMaterialTitle("");
    setMaterialLink("");
    setYoutubeLink("");
    fetchInstructorDataset();
  }

  async function handleDeleteMaterial(id) {
    if (window.confirm("Are you sure you want to delete this course material permanently?")) {
      await deleteDoc(doc(db, "materials", id));
      setCustomAlert({ type: "success", message: "Material deleted." });
      fetchInstructorDataset();
    }
  }

  function startMaterialEdit(m) {
    setEditingMaterialId(m.id);
    setTargetCourse(m.courseId);
    setTargetBatch(m.batchId);
    setMaterialTitle(m.title);
    setMaterialLink(m.link);
    setYoutubeLink(m.youtubeLink);
  }

  /* Mark Roll Call Class Attendance */
  async function handleMarkAttendance(e) {
    e.preventDefault();
    if (!attendanceBatch) return;

    // Future Date Guard: Blocks instructors from logging future roll-call registrations
    const todayStr = new Date().toISOString().split("T")[0];
    if (attendanceDate > todayStr) {
      setCustomAlert({ type: "error", message: "⚠️ Cannot mark attendance for future dates!" });
      return;
    }

    // Check if any student attendance status is left unselected (Select Option)
    const unselectedExist = attendanceFilteredStudents.some(s => !attendanceRecords[s.uid]);
    if (unselectedExist) {
      setCustomAlert({ type: "error", message: "⚠️ Attendance status must be selected for ALL students!" });
      return;
    }

    if (editingAttendanceId) {
      await updateDoc(doc(db, "attendance", editingAttendanceId), {
        records: attendanceRecords,
        timestamp: new Date().toISOString()
      });
      setCustomAlert({ type: "success", message: "Attendance records updated successfully!" });
      setEditingAttendanceId(null);
    } else {
      const exists = pastAttendance.some(sheet => sheet.batchId === attendanceBatch && sheet.date === attendanceDate);
      if (exists) {
        setCustomAlert({ type: "error", message: "⚠️ Attendance already marked." });
        return;
      }

      await addDoc(collection(db, "attendance"), {
        date: attendanceDate,
        batchId: attendanceBatch,
        records: attendanceRecords,
        markedBy: userData.fullName,
        timestamp: new Date().toISOString()
      });
      setCustomAlert({ type: "success", message: "Attendance submitted successfully!" });
    }

    setAttendanceRecords({});
    setAttendanceBatch("");
    setAttendanceCourse("");
    fetchInstructorDataset();
  }

  function startAttendanceEdit(sheet) {
    setEditingAttendanceId(sheet.id);
    const matchedBatchObj = batches.find(b => b.batchName === sheet.batchId);
    const matchedCourseObj = courses.find(c => c.id === matchedBatchObj?.courseId);
    
    setAttendanceCourse(matchedCourseObj ? matchedCourseObj.code : "");
    setAttendanceBatch(sheet.batchId);
    setAttendanceRecords(sheet.records || {});
  }

  /* Questions Builder */
  const addQuestion = () => {
    if (assessmentType === "mcq") {
      setQuestions([...questions, { questionText: "", options: ["", "", "", ""], correctOptionNum: "1", marks: "" }]);
    } else {
      setQuestions([...questions, { questionText: "", marks: "" }]);
    }
  };

  const removeQuestion = (index) => {
    if (questions.length === 1) return;
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const handleQuestionChange = (index, field, value) => {
    const updated = [...questions];
    updated[index][field] = value;
    setQuestions(updated);
  };

  const handleOptionChange = (qIndex, oIndex, value) => {
    const updated = [...questions];
    updated[qIndex].options[oIndex] = value;
    setQuestions(updated);
  };

  function triggerDeploymentConfirmation(e) {
    e.preventDefault();
    if (!assessmentCourse || !assessmentBatch || !assessmentTitle || !assessmentDeadline) return;

    const total = questions.reduce((sum, q) => sum + parseFloat(q.marks || 0), 0);
    setTotalAssessmentMarks(total);
    setShowDeploymentModal(true);
  }

  async function handleCreateAssessment() {
    await addDoc(collection(db, "assessments"), {
      title: assessmentTitle,
      courseId: assessmentCourse,
      batchName: assessmentBatch,
      deadline: assessmentDeadline,
      assessmentType: assessmentType, 
      gradingType: assessmentType === "mcq" ? gradingType : "manual",
      questions: questions, 
      totalMarks: totalAssessmentMarks,
      createdAt: new Date().toISOString()
    });

    setCustomAlert({ type: "success", message: "Assessment exam published successfully!" });
    setShowDeploymentModal(false);
    setAssessmentTitle("");
    setAssessmentDeadline("");
    resetQuestions();
  }

  async function handleDeleteAssessment(id) {
    if (window.confirm("Are you sure you want to permanently delete this assessment exam?")) {
      await deleteDoc(doc(db, "assessments", id));
      setCustomAlert({ type: "success", message: "Assessment exam removed successfully." });
      fetchInstructorDataset();
    }
  }

  /* Settle Grades & Award Marks */
  async function handleGradeSubmission(e) {
    e.preventDefault();
    if (!selectedSubmissionId) return;

    const subObj = submissions.find(s => s.id === selectedSubmissionId);
    const targetExam = assessments.find(a => a.id === subObj.assessmentId);

    let finalCalculatedScore = 0;
    let finalStatus = "graded";

    const isFileExam = (targetExam?.assessmentType === "file") || (subObj?.studentAnswer?.startsWith("http"));

    if (isFileExam) {
      if (!gradeScore) {
        alert("Please enter a score for the project assignment file!");
        return;
      }
      const maxProjectMarks = parseFloat(targetExam?.questions?.[0]?.marks || targetExam?.totalMarks || 100);
      finalCalculatedScore = Math.round((parseFloat(gradeScore) / maxProjectMarks) * 100);
    } else {
      const totalExamMarks = targetExam?.questions?.reduce((sum, q) => sum + parseFloat(q.marks || 0), 0) || 100;
      let obtainedMarks = 0;

      targetExam.questions.forEach((q, idx) => {
        const qMarksAllocated = parseFloat(q.marks || 0);
        
        if (targetExam.assessmentType === "mcq") {
          const isCorrect = manualGradeAnswers[idx] === "Correct";
          if (isCorrect) obtainedMarks += qMarksAllocated;
        } else if (targetExam.assessmentType === "structured") {
          const isCorrect = manualGradeAnswers[idx] === "Correct";
          if (isCorrect) {
            const overrideVal = parseFloat(structuredOverrideScores[idx]);
            obtainedMarks += !isNaN(overrideVal) ? overrideVal : qMarksAllocated;
          }
        }
      });

      finalCalculatedScore = Math.round((obtainedMarks / totalExamMarks) * 100);
    }

    await updateDoc(doc(db, "submissions", selectedSubmissionId), {
      score: finalCalculatedScore,
      feedback: gradeFeedback || "Graded",
      status: finalStatus
    });

    await addDoc(collection(db, "announcements"), {
      title: "Submission Graded",
      content: `Your exam "${subObj ? subObj.assessmentTitle : "Task"}" has been graded. Score: ${finalCalculatedScore}/100.`,
      targetType: "personal_student",
      targetStudentUid: subObj.studentId, // Stores student's auth UID
      targetRole: "student",
      publishedBy: "System Guard",
      publishedByRole: "system",
      timestamp: new Date().toISOString()
    });

    setCustomAlert({ type: "success", message: `Graded successfully! Final Score: ${finalCalculatedScore}/100` });
    setSelectedSubmissionId(null);
    setGradeScore("");
    setGradeFeedback("");
    setManualGradeAnswers({});
    setStructuredOverrideScores({});
    fetchInstructorDataset();
  }

  /* Export Graded Results to PDF */
  const handleExportGradedPDF = () => {
    const gradedList = mySubmissions.filter(sub => sub.status === "graded");
    const headers = ["Student Name", "Assessment Topic", "Final Score"];
    
    const rows = gradedList.map(sub => [
      sub.studentName,
      sub.assessmentTitle || "Structured Task",
      `${sub.score}/100`
    ]);

    const dateStr = new Date().toLocaleDateString();
    downloadAsPDF(
      `graded_results_report_${dateStr.replace(/\//g, "-")}.pdf`,
      "Oncode APT - Graded Results Summary",
      `Report Generated: ${dateStr} - Evaluation History`,
      headers,
      rows
    );
  };

  /* Post Class Announcements */
  async function handlePublishClassAnnouncement(e) {
    e.preventDefault();
    if (!announceCourse || !announceBatch) return;

    if (editingAnnounceId) {
      await updateDoc(doc(db, "announcements", editingAnnounceId), {
        title: announceTitle,
        content: announceContent,
        targetCourseId: announceCourse,
        targetBatchId: announceBatch
      });
      setCustomAlert({ type: "success", message: "Class announcement updated." });
      setEditingAnnounceId(null);
    } else {
      await addDoc(collection(db, "announcements"), {
        title: announceTitle,
        content: announceContent,
        targetType: "batch",
        targetCourseId: announceCourse,
        targetBatchId: announceBatch,
        publishedBy: userData.fullName,
        publishedByRole: "instructor",
        timestamp: new Date().toISOString()
      });
      setCustomAlert({ type: "success", message: "Announcement published!" });
    }

    setAnnounceTitle("");
    setAnnounceContent("");
    setAnnounceCourse("");
    setAnnounceBatch("");
    fetchInstructorDataset();
  }

  async function handleEditAnnouncement(ann) {
    setEditingAnnounceId(ann.id);
    setAnnounceCourse(ann.targetCourseId);
    setAnnounceBatch(ann.targetBatchId);
    setAnnounceTitle(ann.title);
    setAnnounceContent(ann.content);
  }

  async function handleDeleteAnnouncement(id) {
    if (window.confirm("Are you sure you want to permanently delete this announcement?")) {
      await deleteDoc(doc(db, "announcements", id));
      setCustomAlert({ type: "success", message: "Announcement deleted." });
      fetchInstructorDataset();
    }
  }

  /* Send Student Enrollment Requests */
  async function handleEnrollmentRequest(e) {
    e.preventDefault();
    if (!mapStudentId || !mapCourseCode || !mapBatchName) return;

    await addDoc(collection(db, "enrollmentRequests"), {
      studentId: mapStudentId,
      courseCode: mapCourseCode,
      batchName: mapBatchName,
      instructorName: userData.fullName,
      status: "pending",
      timestamp: new Date().toISOString()
    });

    setCustomAlert({ type: "success", message: "Enrollment request submitted successfully!" });
    setMapStudentId("");
    setMapCourseCode("");
    setMapBatchName("");
  }

  /* =======================================
      IN-MEMORY FILTER PRESETS & UTILITIES
     ======================================= */
  const myStudents = students.filter(s => {
    const enrolls = s.enrollments || [];
    return enrolls.some(en => (userData.assignedCourses || []).includes(en.courseCode));
  });

  const attendanceFilteredStudents = myStudents.filter(s => {
    const enrolls = s.enrollments || [];
    return enrolls.some(en => en.batchName === attendanceBatch);
  });

  // Fallback filter to automatically retrieve courseCode from assessments if missing in submission doc
  const mySubmissions = submissions.filter(sub => {
    const cCode = sub.courseCode || assessments.find(a => a.id === sub.assessmentId)?.courseId;
    return (userData?.assignedCourses || []).includes(cCode);
  });

  /* PDF Stream Construction Utility */
  const downloadAsPDF = (filename, title, subtitle, headers, rows) => {
    let pdfContent = `%PDF-1.4\n`;
    pdfContent += `1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n`;
    pdfContent += `2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n`;
    pdfContent += `3 0 obj\n<< /Type /Page /Parent 2 0 R /Resources << /Font << /F1 4 0 R /F2 6 0 R >> >> /MediaBox [0 0 595 842] /Contents 5 0 R >>\nendobj\n`;
    pdfContent += `4 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>\nendobj\n`;
    pdfContent += `6 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>\nendobj\n`;
    
    let stream = ``;
    stream += `0.5 w\n`;
    stream += `0.85 0.85 0.85 RG\n`;
    stream += `BT\n/F1 15 Tf\n1 0 0 1 40 780 Tm\n(${title}) Tj\nET\n`;
    stream += `BT\n/F2 9.5 Tf\n1 0 0 1 40 758 Tm\n(${subtitle}) Tj\nET\n`;
    stream += `40 742 m\n555 742 l\nS\n`;
    stream += `BT\n/F1 10 Tf\n`;
    stream += `1 0 0 1 45 727 Tm\n(${headers[0]}) Tj\n`;
    stream += `1 0 0 1 185 727 Tm\n(${headers[1]}) Tj\n`;
    stream += `1 0 0 1 425 727 Tm\n(${headers[2]}) Tj\n`;
    stream += `ET\n`;
    stream += `0.8 w\n0.4 0.4 0.4 RG\n`;
    stream += `40 718 m\n555 718 l\nS\n`;
    stream += `0.4 w\n0.85 0.85 0.85 RG\n`;
    
    const wrapText = (text, maxLength) => {
      const words = text.split(" ");
      const lines = [];
      let currentLine = "";

      words.forEach(word => {
        if ((currentLine + " " + word).trim().length <= maxLength) {
          currentLine = (currentLine + " " + word).trim();
        } else {
          lines.push(currentLine);
          currentLine = word;
        }
      });
      if (currentLine) {
        lines.push(currentLine);
      }
      return lines;
    };

    let y = 693;
    rows.forEach((row) => {
      if (y > 50) {
        const col1 = String(row[0] || "").replace(/[\(\)]/g, "");
        const col2 = String(row[1] || "").replace(/[\(\)]/g, "");
        const col3 = String(row[2] || "").replace(/[\(\)]/g, "");
        
        const wrappedCol1 = wrapText(col1, 15);
        const wrappedCol2 = wrapText(col2, 40);
        const linesCount = Math.max(wrappedCol1.length, wrappedCol2.length);
        const centerOffset = ((linesCount - 1) * 12) / 2;
        
        stream += `BT\n/F2 9 Tf\n`;
        stream += `1 0 0 1 425 ${y - centerOffset} Tm\n(${col3}) Tj\n`;
        stream += `ET\n`;
        
        wrappedCol1.forEach((line, lineIdx) => {
          stream += `BT\n/F2 9 Tf\n1 0 0 1 45 ${y - (lineIdx * 12)} Tm\n(${line}) Tj\nET\n`;
        });

        wrappedCol2.forEach((line, lineIdx) => {
          stream += `BT\n/F2 9 Tf\n1 0 0 1 185 ${y - (lineIdx * 12)} Tm\n(${line}) Tj\nET\n`;
        });
        
        const bottomY = y - ((linesCount - 1) * 12) - 8;
        stream += `40 ${bottomY} m\n555 ${bottomY} l\nS\n`;
        
        y = bottomY - 18;
      }
    });
    
    pdfContent += `5 0 obj\n<< /Length ${stream.length} >>\nstream\n${stream}\nendstream\nendobj\n`;
    pdfContent += `xref\n0 7\n0000000000 65535 f\n0000000009 00000 n\n0000000056 00000 n\n0000000111 00000 n\n0000000212 00000 n\n0000000293 00000 n\n`;
    pdfContent += `trailer\n<< /Size 7 /Root 1 0 R >>\nstartxref\n${pdfContent.length}\n%%EOF`;
    
    const blob = new Blob([pdfContent], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sortedPastAttendance = [...pastAttendance].sort((a, b) => {
    if (a.date !== b.date) {
      return new Date(a.date) - new Date(b.date);
    }
    return a.batchId?.localeCompare(b.batchId);
  });

  /* YouTube URL to Embed Converter */
  const getEmbedYoutubeUrl = (url) => {
    if (!url) return null;
    try {
      // Handle short share links
      if (url.includes("youtu.be/")) {
        const id = url.split("youtu.be/")[1]?.split(/[?#]/)[0];
        return id ? `https://www.youtube.com/embed/${id}` : null;
      }
      
      // Handle standard watch links and shorts
      if (url.includes("youtube.com")) {
        const urlObj = new URL(url);

        // YouTube Shorts video link
        if (urlObj.pathname.includes("/shorts/")) {
          const id = urlObj.pathname.split("/shorts/")[1]?.split(/[?#]/)[0];
          return id ? `https://www.youtube.com/embed/${id}` : null;
        }
        // standard video link
        const id = urlObj.searchParams.get("v");
        return id ? `https://www.youtube.com/embed/${id}` : null;
      }
      
      // Handle direct embed link formats
      if (url.includes("/embed/")) {
        return url;
      }
    } catch (e) {
      console.error("Failed to parse YouTube URL: ", e);
    }
    return null;
  };

  async function markAsRead(id) {
    const persistentReads = userData.readNotifications || [];
    if (!persistentReads.includes(id)) {
      const updatedReads = [...persistentReads, id];
      await updateDoc(doc(db, "users", userData.uid), {
        readNotifications: updatedReads
      });
      fetchInstructorDataset();
    }
  }

  // Dynamic styling helper for top sub-sections navigation
  const subTabStyle = (isActive) => ({
    padding: "0.5rem 1rem",
    fontSize: "0.85rem",
    fontWeight: "600",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "all 0.2s",
    background: isActive ? "var(--navy-primary)" : "transparent",
    color: isActive ? "#ffffff" : "var(--text-muted)"
  });

  /* ======================
      PORTAL VIEW RENDERER
     ====================== */
  return (
    <div>
      {/* Custom Alert Pop-up Modal */}
      {customAlert && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(15,23,42,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 999, padding: "1.5rem" }}>
          <div className="card" ref={alertModalRef} style={{ maxWidth: "450px", width: "100%", padding: "2rem", textAlign: "center", position: "relative" }}>
            <button onClick={() => setCustomAlert(null)} style={{ position: "absolute", top: "1rem", right: "1rem", background: "none", border: "none", color: "var(--danger)", cursor: "pointer" }}><X size={20} /></button>
            <h3 style={{ color: customAlert.type === "success" ? "var(--success)" : "var(--danger)", marginBottom: "1rem" }}>
              {customAlert.type === "success" ? "Success!" : "Notice / Error"}
            </h3>
            <p style={{ color: "var(--text-main)", marginBottom: "1.5rem" }}>{customAlert.message}</p>
            <button className="navy-btn" onClick={() => setCustomAlert(null)} style={{ margin: "0 auto" }}>Close Alert</button>
          </div>
        </div>
      )}

      {/* Confirm Assessment Deployment Modal */}
      {showDeploymentModal && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(15,23,42,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 999, padding: "1.5rem" }}>
          <div className="card" ref={deploymentModalRef} style={{ maxWidth: "600px", width: "100%", padding: "2rem", maxHeight: "90vh", overflowY: "auto", position: "relative" }}>
            <button onClick={() => setShowDeploymentModal(false)} style={{ position: "absolute", top: "1rem", right: "1rem", background: "none", border: "none", color: "var(--danger)", cursor: "pointer" }}><X size={20} /></button>
            <h3 style={{ color: "var(--navy-primary)", marginBottom: "1rem" }}>Confirm Assessment Pubish</h3>
            <p style={{ marginBottom: "1.25rem" }}>Review the total calculated marks before publishing:</p>
            
            <div style={{ background: "var(--bg-slate)", padding: "1rem", borderRadius: "8px", marginBottom: "1.5rem" }}>
              <p><strong>Exam Title:</strong> {assessmentTitle}</p>
              <p><strong>Target Course Code:</strong> {assessmentCourse}</p>
              <p><strong>Target Batch Group:</strong> {assessmentBatch}</p>
              <p><strong>Total Allocated Marks:</strong> {totalAssessmentMarks}</p>
            </div>

            <h4 style={{ marginBottom: "0.75rem" }}>Allocated Questions</h4>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginBottom: "2rem" }}>
              {questions.map((q, idx) => {
                const qMarks = parseFloat(q.marks || 0);
                const weightage = totalAssessmentMarks > 0 ? ((qMarks / totalAssessmentMarks) * 100).toFixed(1) : "0.0";
                return (
                  <div key={idx} style={{ borderBottom: "1px solid var(--border-color)", paddingBottom: "0.5rem" }}>
                    <p><strong>Q{idx + 1}:</strong> {q.questionText}</p>
                    <small style={{ color: "var(--text-muted)" }}>Marks: {qMarks} | Percentage: <strong>{weightage}% of total</strong></small>
                  </div>
                );
              })}
            </div>

            <div style={{ display: "flex", gap: "1rem" }}>
              <button className="navy-btn" onClick={handleCreateAssessment} style={{ background: "var(--success)" }}>Confirm & Publish Assessment</button>
              <button className="navy-btn navy-btn-danger" onClick={() => setShowDeploymentModal(false)}>Go Back / Edit</button>
            </div>
          </div>
        </div>
      )}

      {/* Video Player Modal Panel (With Inline & External Fallback) */}
      {activeVideoUrl && (
        <div className="card" style={{ border: "2px solid var(--navy-primary)", padding: "1.5rem", position: "relative", marginBottom: "2rem" }}>
          <button 
            onClick={() => { setActiveVideoUrl(""); setActiveVideoType(""); }}
            style={{ position: "absolute", top: "1rem", right: "1rem", background: "none", border: "none", color: "var(--danger)", cursor: "pointer" }}
          >
            <X size={20} />
          </button>
          <h4 style={{ marginBottom: "1rem" }}>Inline Class Player</h4>
          
          <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden", borderRadius: "8px", background: "#000" }}>
            {activeVideoType === "youtube" ? (
              <iframe 
                src={getEmbedYoutubeUrl(activeVideoUrl)} 
                style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0 }} 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen 
                title="Class Video"
              />
            ) : (
              <video 
                src={activeVideoUrl} 
                controls 
                style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
              />
            )}
          </div>

          {activeVideoType === "youtube" && (
            <div style={{ marginTop: "1rem", textAlign: "center" }}>
              <p style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: "0.5rem" }}>
                Having trouble playing inline? Some videos are restricted by YouTube.
              </p>
              <a 
                href={activeVideoUrl} 
                target="_blank" 
                rel="noreferrer" 
                className="navy-btn" 
                style={{ fontSize: "0.85rem", padding: "0.5rem 1.25rem", background: "var(--navy-light)", textDecoration: "none", display: "inline-flex" }}
              >
                Open Externally on YouTube ↗
              </a>
            </div>
          )}
        </div>
      )}

      {/* Sub-sections Navigation Header Men */}
      <div className="sub-sections-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e2e8f0", paddingBottom: "1rem", marginBottom: "1.5rem" }}>
        <div className="sub-tabs-container" style={{ display: "flex", gap: "0.5rem" }}>
          
          {activeTab === "dashboard" && (
            <>
              <button className={`sub-tab-btn ${subTab === "overview" ? "active" : ""}`} onClick={() => setSubTab("overview")} style={subTabStyle(subTab === "overview")}>Overview</button>
              <button className={`sub-tab-btn ${subTab === "enroll-request" ? "active" : ""}`} onClick={() => setSubTab("enroll-request")} style={subTabStyle(subTab === "enroll-request")}>Enrollment Request</button>
            </>
          )}

          {activeTab === "materials" && (
            <>
              <button className={`sub-tab-btn ${subTab === "upload-materials" ? "active" : ""}`} onClick={() => setSubTab("upload-materials")} style={subTabStyle(subTab === "upload-materials")}>Upload Materials</button>
              <button className={`sub-tab-btn ${subTab === "materials-list" ? "active" : ""}`} onClick={() => setSubTab("materials-list")} style={subTabStyle(subTab === "materials-list")}>Materials History</button>
            </>
          )}

          {activeTab === "attendance" && (
            <>
              <button className={`sub-tab-btn ${subTab === "mark-attendance" ? "active" : ""}`} onClick={() => setSubTab("mark-attendance")} style={subTabStyle(subTab === "mark-attendance")}>Mark Attendance</button>
              <button className={`sub-tab-btn ${subTab === "attendance-history" ? "active" : ""}`} onClick={() => setSubTab("attendance-history")} style={subTabStyle(subTab === "attendance-history")}>Attendance History</button>
            </>
          )}

          {activeTab === "assessments" && (
            <>
              <button className={`sub-tab-btn ${subTab === "create-assessment" ? "active" : ""}`} onClick={() => setSubTab("create-assessment")} style={subTabStyle(subTab === "create-assessment")}>Create Assessment</button>
              <button className={`sub-tab-btn ${subTab === "deployed-assessments" ? "active" : ""}`} onClick={() => setSubTab("deployed-assessments")} style={subTabStyle(subTab === "deployed-assessments")}>Published Assessments</button>
            </>
          )}

          {activeTab === "grading" && (
            <>
              <button className={`sub-tab-btn ${subTab === "new-grading" ? "active" : ""}`} onClick={() => setSubTab("new-grading")} style={subTabStyle(subTab === "new-grading")}>New Grading</button>
              <button className={`sub-tab-btn ${subTab === "already-graded" ? "active" : ""}`} onClick={() => setSubTab("already-graded")} style={subTabStyle(subTab === "already-graded")}>Graded History</button>
            </>
          )}

          {activeTab === "announcements" && (
            <>
              <button className={`sub-tab-btn ${subTab === "broadcast" ? "active" : ""}`} onClick={() => setSubTab("broadcast")} style={subTabStyle(subTab === "broadcast")}>New Announcement</button>
              <button className={`sub-tab-btn ${subTab === "sent-history" ? "active" : ""}`} onClick={() => setSubTab("sent-history")} style={subTabStyle(subTab === "sent-history")}>Announcement History</button>
              <button className={`sub-tab-btn ${subTab === "received" ? "active" : ""}`} onClick={() => setSubTab("received")} style={subTabStyle(subTab === "received")}>Received Announcements</button>
            </>
          )}

        </div>
      </div>

      {/* ===========================
           PORTAL DATA VIEWS & FORMS
          =========================== */}

      {/* Dashboard View Tab */}
      {activeTab === "dashboard" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
          
          {subTab === "overview" && (
            <>
              <div className="grid-matrix">
                <div className="card" style={{ textAlign: "center", borderTop: "4px solid var(--navy-primary)", padding: "1.5rem" }}>
                  <h3 style={{ fontSize: "1.1rem", color: "var(--text-muted)", marginBottom: "0.5rem" }}>My Assigned Courses</h3>
                  <p style={{ fontSize: "2.5rem", fontWeight: "bold", color: "var(--navy-primary)", margin: 0 }}>{assignedCourses.length}</p>
                </div>
                <div className="card" style={{ textAlign: "center", borderTop: "4px solid var(--warning)", padding: "1.5rem" }}>
                  <h3 style={{ fontSize: "1.1rem", color: "var(--text-muted)", marginBottom: "0.5rem" }}>My Assigned Batches</h3>
                  <p style={{ fontSize: "2.5rem", fontWeight: "bold", color: "var(--warning)", margin: 0 }}>{assignedBatches.length}</p>
                </div>
                <div className="card" style={{ textAlign: "center", borderTop: "4px solid var(--success)", padding: "1.5rem" }}>
                  <h3 style={{ fontSize: "1.1rem", color: "var(--text-muted)", marginBottom: "0.5rem" }}>Total Students Enrolled</h3>
                  <p style={{ fontSize: "2.5rem", fontWeight: "bold", color: "var(--success)", margin: 0 }}>{myStudents.length}</p>
                </div>
              </div>

              <div className="card">
                <h3 className="card-title">My Assigned Course Batches</h3>
                <div className="table-container">
                  <table className="custom-table">
                    <thead>
                      <tr>
                        <th>Course Code & Name</th>
                        <th>My Batches</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assignedCourses.map(c => {
                        const mappedBatches = assignedBatches.filter(b => b.courseId === c.id);
                        return (
                          <tr key={c.id}>
                            <td><strong>{c.code}</strong> - {c.name}</td>
                            <td>
                              {mappedBatches.map(b => (
                                <span key={b.id} className="badge badge-approved" style={{ marginRight: "0.25rem", background: "#f1f5f9", color: "#334155", border: "1px solid #cbd5e1" }}>
                                  {b.batchName}
                                </span>
                              ))}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {subTab === "enroll-request" && (
            <div className="card" style={{ borderTop: "4px solid var(--navy-light)" }}>
              <h3 className="card-title">Assign Student to Course Request</h3>
              <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", marginBottom: "1.25rem" }}>
                Instructors can map students to courses. These mapping files will be finalized once Admin approves them in Onboarding approvals.
              </p>
              <form onSubmit={handleEnrollmentRequest}>
                <div className="form-group">
                  <label>Select Student</label>
                  <select className="form-control" value={mapStudentId} onChange={(e) => setMapStudentId(e.target.value)} required>
                    <option value="">Select Student</option>
                    {students.filter(u => u.status === "approved").map(s => (
                      <option key={s.id} value={s.uid}>{s.fullName} ({s.customId})</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Select Course</label>
                  <select className="form-control" value={mapCourseCode} onChange={(e) => setMapCourseCode(e.target.value)} required>
                    <option value="">Select Course</option>
                    {assignedCourses.map(c => (
                      <option key={c.id} value={c.code}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Select Batch</label>
                  <select className="form-control" value={mapBatchName} onChange={(e) => setMapBatchName(e.target.value)} required>
                    <option value="">Select Batch</option>
                    {assignedBatches.filter(b => b.courseId === courses.find(c => c.code === mapCourseCode)?.id).map(b => (
                      <option key={b.id} value={b.batchName}>{b.batchName}</option>
                    ))}
                  </select>
                </div>
                <button type="submit" className="navy-btn" style={{ background: "var(--navy-light)" }}>Request Approval</button>
              </form>
            </div>
          )}

        </div>
      )}

      {/* Materials View Tab */}
      {activeTab === "materials" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
          
          {subTab === "upload-materials" && (
            <div className="card" style={{ borderTop: "4px solid var(--navy-primary)" }}>
              <h3 className="card-title">Upload Lessons & Materials</h3>
              <form onSubmit={handleMaterialUpload}>
                <div className="form-group">
                  <label>Select Course</label>
                  <select className="form-control" value={targetCourse} onChange={(e) => setTargetCourse(e.target.value)} required>
                    <option value="">Select Course</option>
                    {assignedCourses.map(c => (
                      <option key={c.id} value={c.code}>{c.name} ({c.code})</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Select Batch</label>
                  <select className="form-control" value={targetBatch} onChange={(e) => setTargetBatch(e.target.value)}>
                    <option value="">Select Batches</option>
                    {assignedBatches.filter(b => b.courseId === courses.find(c => c.code === targetCourse)?.id).map(b => (
                      <option key={b.id} value={b.batchName}>{b.batchName}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Title</label>
                  <input type="text" className="form-control" value={materialTitle} onChange={(e) => setMaterialTitle(e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>YouTube Video Link</label>
                  <input type="url" className="form-control" value={youtubeLink} onChange={(e) => setYoutubeLink(e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Choose File Document (upto 500MB)</label>
                  <input type="file" className="form-control" onChange={handleRealStorageUpload} />
                  {fileValidationMessage && (
                    <small style={{ color: "var(--danger)", fontWeight: "bold", display: "block", marginTop: "0.5rem" }}>{fileValidationMessage}</small>
                  )}
                  {uploading && <small style={{ color: "var(--success)", display: "block", marginTop: "0.5rem" }}>Uploading file...</small>}
                </div>
                <button type="submit" className="navy-btn" disabled={fileValidationMessage !== "" || uploading}>Publish</button>
              </form>
            </div>
          )}

          {subTab === "materials-list" && (
            <div className="card">
              <h3 className="card-title">Meterial History</h3>
              <div className="table-container">
                <table className="custom-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Course</th>
                      <th>Resource</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {materials.filter(m => (userData.assignedCourses || []).includes(m.courseId)).map(m => (
                      <tr key={m.id}>
                        <td><strong>{m.title}</strong></td>
                        <td>{m.courseId} ({m.batchId})</td>
                        <td>
                          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                            {m.link && (
                              m.link.endsWith(".mp4") ? (
                                <button className="navy-btn" style={{ padding: "0.35rem 0.75rem", fontSize: "0.8rem", background: "var(--success)" }} onClick={() => { setActiveVideoUrl(m.link); setActiveVideoType("mp4"); }}>Play Video</button>
                              ) : (
                                <a 
                                  href={m.link} 
                                  target="_blank" 
                                  rel="noreferrer" 
                                  className="navy-btn" 
                                  style={{ textDecoration: "none", fontSize: "0.8rem", padding: "0.35rem 0.75rem", background: "var(--navy-primary)" }}
                                >
                                  Download Document
                                </a>
                              )
                            )}
                            
                            {m.youtubeLink && (
                              <button className="navy-btn" style={{ padding: "0.35rem 0.75rem", fontSize: "0.8rem", background: "var(--danger)" }} onClick={() => { setActiveVideoUrl(m.youtubeLink); setActiveVideoType("youtube"); }}>Play Youtube Video</button>
                            )}
                          </div>
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: "0.5rem" }}>
                            <button className="navy-btn" style={{ padding: "0.4rem", background: "var(--navy-light)" }} onClick={() => startMaterialEdit(m)}><Edit size={14} /></button>
                            <button className="navy-btn navy-btn-danger" style={{ padding: "0.4rem" }} onClick={() => handleDeleteMaterial(m.id)}><Trash2 size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {editingMaterialId !== null && (
            <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(15,23,42,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 999, padding: "1.5rem" }}>
              <div className="card" style={{ maxWidth: "550px", width: "100%", padding: "2rem", position: "relative", borderTop: "4px solid var(--navy-primary)" }}>
                
                <button 
                  onClick={() => { setEditingMaterialId(null); setMaterialTitle(""); setMaterialLink(""); setYoutubeLink(""); }} 
                  style={{ position: "absolute", top: "1.2rem", right: "1.2rem", background: "none", border: "none", color: "var(--danger)", cursor: "pointer" }}
                >
                  <X size={20} />
                </button>
                
                <h3 className="card-title" style={{ marginBottom: "1.5rem", fontSize: "1.2rem", fontWeight: "600" }}>Modify Material</h3>
                
                <form onSubmit={handleMaterialUpload}>
                  <div className="form-group">
                    <label>Title</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={materialTitle} 
                      onChange={(e) => setMaterialTitle(e.target.value)} 
                      required 
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>YouTube Video Link</label>
                    <input 
                      type="url" 
                      className="form-control" 
                      value={youtubeLink} 
                      onChange={(e) => setYoutubeLink(e.target.value)} 
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Choose File Document (upto 500MB)</label>
                    <input type="file" className="form-control" onChange={handleRealStorageUpload} />
                    {fileValidationMessage && (
                      <small style={{ color: "var(--danger)", fontWeight: "bold", display: "block", marginTop: "0.5rem" }}>{fileValidationMessage}</small>
                    )}
                    {uploading && <small style={{ color: "var(--success)", display: "block", marginTop: "0.5rem" }}>Uploading file...</small>}
                  </div>
                  
                  <div style={{ display: "flex", gap: "0.5rem", marginTop: "1.5rem" }}>
                    <button type="submit" className="navy-btn" disabled={fileValidationMessage !== "" || uploading}>Update Material</button>
                    <button 
                      type="button" 
                      className="navy-btn navy-btn-danger" 
                      onClick={() => { setEditingMaterialId(null); setMaterialTitle(""); setMaterialLink(""); setYoutubeLink(""); }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>

              </div>
            </div>
          )}

        </div>
      )}

      {/* Attendance View Tab */}
      {activeTab === "attendance" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
          
          {subTab === "mark-attendance" && (
            <div className="card" style={{ borderTop: "4px solid var(--warning)" }}>
              <h3 className="card-title">Mark Attendance</h3>
              
              <div className="form-group">
                <label>Select Course</label>
                <select 
                  className="form-control" 
                  value={attendanceCourse} 
                  onChange={(e) => {
                    setAttendanceCourse(e.target.value);
                    setAttendanceBatch(""); 
                    setAttendanceRecords({}); 
                  }} 
                  required 
                >
                  <option value="">Select Course</option>
                  {assignedCourses.map(c => (
                    <option key={c.id} value={c.code}>{c.name} ({c.code})</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Select Batch</label>
                <select 
                  className="form-control" 
                  value={attendanceBatch} 
                  onChange={(e) => {
                    setAttendanceBatch(e.target.value);
                    setAttendanceRecords({}); 
                  }} 
                  required 
                  disabled={!attendanceCourse}
                >
                  <option value="">Select Batch</option>
                  {assignedBatches
                    .filter(b => b.courseId === courses.find(c => c.code === attendanceCourse)?.id)
                    .map(b => (
                      <option key={b.id} value={b.batchName}>{b.batchName}</option>
                    ))
                  }
                </select>
              </div>

              <div className="form-group">
                <label>Class Date</label>
                <input type="date" className="form-control" value={attendanceDate} onChange={(e) => setAttendanceDate(e.target.value)} required />
              </div>

              {attendanceBatch && attendanceFilteredStudents.length === 0 ? (
                <div style={{ color: "var(--danger)", fontWeight: "bold", margin: "1rem 0" }}>
                  ⚠️ No students enrolled in Batch "{attendanceBatch}" yet!
                </div>
              ) : (
                <>
                  {attendanceBatch && (
                    <div className="table-container" style={{ marginBottom: "1.5rem" }}>
                      <table className="custom-table">
                        <thead>
                          <tr>
                            <th>Student Name & ID</th>
                            <th>Attendance Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {attendanceFilteredStudents.map(s => (
                            <tr key={s.id}>
                              <td><strong>{s.fullName}</strong> ({s.customId})</td>
                              <td>
                                <select className="form-control" style={{ maxWidth: "150px" }} value={attendanceRecords[s.uid] || ""} onChange={(e) => setAttendanceRecords({ ...attendanceRecords, [s.uid]: e.target.value })}>
                                  <option value="">Select Option</option>
                                  <option value="Present">Present</option>
                                  <option value="Absent">Absent</option>
                                </select>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {attendanceBatch && (
                    <button className="navy-btn" onClick={handleMarkAttendance}>Submit Attendance</button>
                  )}
                </>
              )}
            </div>
          )}

          {subTab === "attendance-history" && (
            <div className="card">
              <h3 className="card-title">View Attendance History</h3>
              <div className="table-container">
                <table className="custom-table">
                  <thead>
                    <tr>
                      <th>Class Date</th>
                      <th>Batch</th>
                      <th>Present Students</th>
                      <th>Absent Students</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPastAttendance.filter(sheet => assignedBatches.some(b => b.batchName === sheet.batchId)).map(sheet => {
                      const presentNames = [];
                      const absentNames = [];
                      
                      if (sheet.records) {
                        Object.keys(sheet.records).forEach(uid => {
                          const studentObj = students.find(s => s.uid === uid);
                          if (studentObj) {
                            if (sheet.records[uid] === "Present") {
                              presentNames.push(studentObj.fullName);
                            } else {
                              absentNames.push(studentObj.fullName);
                            }
                          }
                        });
                      }

                      return (
                        <tr key={sheet.id}>
                          <td><strong>{sheet.date}</strong></td>
                          <td><span className="badge badge-approved" style={{ background: "#cbd5e1", color: "#1e293b" }}>{sheet.batchId}</span></td>
                          <td>
                            {presentNames.map((n, idx) => (
                              <span key={idx} className="badge badge-approved" style={{ marginRight: "0.25rem" }}>{n}</span>
                            ))}
                          </td>
                          <td>
                            {absentNames.map((n, idx) => (
                              <span key={idx} className="badge badge-pending" style={{ marginRight: "0.25rem", background: "#fee2e2", color: "#991b1b" }}>{n}</span>
                            ))}
                          </td>
                          <td>
                            <button className="navy-btn" style={{ padding: "0.35rem 0.75rem", fontSize: "0.85rem" }} onClick={() => startAttendanceEdit(sheet)}>Edit</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {editingAttendanceId !== null && (
            <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(15,23,42,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 999, padding: "1.5rem" }}>
              <div className="card" style={{ maxWidth: "600px", width: "100%", padding: "2rem", position: "relative", borderTop: "4px solid var(--warning)", maxHeight: "90vh", overflowY: "auto" }}>
                
                <button 
                  onClick={() => { setEditingAttendanceId(null); setAttendanceBatch(""); setAttendanceCourse(""); setAttendanceRecords({}); }} 
                  style={{ position: "absolute", top: "1.2rem", right: "1.2rem", background: "none", border: "none", color: "var(--danger)", cursor: "pointer" }}
                >
                  <X size={20} />
                </button>
                
                <h3 className="card-title" style={{ marginBottom: "1.5rem", fontSize: "1.2rem", fontWeight: "600" }}>Modify Attendance</h3>
                
                <div className="form-group">
                  <label>Assigned Batch</label>
                  <input type="text" className="form-control" value={attendanceBatch} disabled style={{ backgroundColor: "#e2e8f0", color: "#64748b", cursor: "not-allowed" }} />
                </div>
                
                <div className="form-group">
                  <label>Class Date</label>
                  <input type="date" className="form-control" value={attendanceDate} disabled style={{ backgroundColor: "#e2e8f0", color: "#64748b", cursor: "not-allowed" }} />
                </div>

                <div className="table-container" style={{ marginBottom: "1.5rem" }}>
                  <table className="custom-table">
                    <thead>
                      <tr>
                        <th>Student Name & ID</th>
                        <th>Attendance Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendanceFilteredStudents.map(s => (
                        <tr key={s.id}>
                          <td><strong>{s.fullName}</strong> ({s.customId})</td>
                          <td>
                            <select className="form-control" style={{ maxWidth: "150px" }} value={attendanceRecords[s.uid] || ""} onChange={(e) => setAttendanceRecords({ ...attendanceRecords, [s.uid]: e.target.value })}>
                              <option value="">Select Option</option>
                              <option value="Present">Present</option>
                              <option value="Absent">Absent</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <button className="navy-btn" onClick={handleMarkAttendance}>Update Attendance</button>
                  <button 
                    type="button" 
                    className="navy-btn navy-btn-danger" 
                    onClick={() => { setEditingAttendanceId(null); setAttendanceBatch(""); setAttendanceCourse(""); setAttendanceRecords({}); }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      )}

      {/* Assessments View Tab */}
      {activeTab === "assessments" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
          
          {subTab === "create-assessment" && (
            <div className="card" style={{ borderTop: "4px solid var(--navy-light)" }}>
              <h3 className="card-title">Create Assessment</h3>
              <form onSubmit={triggerDeploymentConfirmation}>
                <div className="form-group">
                  <label>Select Course</label>
                  <select className="form-control" value={assessmentCourse} onChange={(e) => setAssessmentCourse(e.target.value)} required>
                    <option value="">Select Course</option>
                    {assignedCourses.map(c => (
                      <option key={c.id} value={c.code}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Select Batch</label>
                  <select className="form-control" value={assessmentBatch} onChange={(e) => setAssessmentBatch(e.target.value)} required>
                    <option value="">Select Batch</option>
                    {assignedBatches.filter(b => b.courseId === courses.find(c => c.code === assessmentCourse)?.id).map(b => (
                      <option key={b.id} value={b.batchName}>{b.batchName}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Title</label>
                  <input type="text" className="form-control" value={assessmentTitle} onChange={(e) => setAssessmentTitle(e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>Submission Deadline</label>
                  <input type="datetime-local" className="form-control" value={assessmentDeadline} onChange={(e) => setAssessmentDeadline(e.target.value)} required />
                </div>

                <div className="form-group" style={{ maxWidth: "300px" }}>
                  <label>Select Assessment Type</label>
                  <select 
                    className="form-control" 
                    value={assessmentType} 
                    onChange={(e) => {
                      const val = e.target.value;
                      setAssessmentType(val);
                      if (val === "mcq") {
                        setQuestions([{ questionText: "", options: ["", "", "", ""], correctOptionNum: "1", marks: "" }]);
                      } else if (val === "file") {
                        setQuestions([{ questionText: "Instructions: Please upload your project assignment file in the upload box below.", marks: "100" }]);
                      } else {
                        setQuestions([{ questionText: "", marks: "" }]);
                      }
                    }}
                  >
                    <option value="mcq">MCQ Quiz</option>
                    <option value="structured">Structured Answer</option>
                    <option value="file">Project Submission</option>
                  </select>
                </div>

                {assessmentType === "mcq" && (
                  <div className="form-group" style={{ maxWidth: "300px" }}>
                    <label>Select Grading Type</label>
                    <select className="form-control" value={gradingType} onChange={(e) => setGradingType(e.target.value)}>
                      <option value="auto">Auto-Grading</option>
                      <option value="manual">Manual Grading</option>
                    </select>
                  </div>
                )}

                <div style={{ marginTop: "2rem", borderTop: "2px dashed var(--border-color)", paddingTop: "1.5rem" }}>
                  <h4 style={{ marginBottom: "1rem" }}>Questions</h4>
                  {questions.map((q, qIndex) => (
                    <div key={qIndex} className="card" style={{ backgroundColor: "#f8fafc", borderLeft: "5px solid var(--navy-light)", position: "relative" }}>
                      {assessmentType !== "file" && (
                        <button 
                          type="button" 
                          onClick={() => removeQuestion(qIndex)} 
                          style={{ position: "absolute", top: "1rem", right: "1rem", background: "none", border: "none", color: "var(--danger)", cursor: "pointer" }}
                          title="Remove Question"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}

                      <div className="form-group">
                        <label>Question {qIndex + 1}</label>
                        <input type="text" className="form-control" value={q.questionText} onChange={(e) => handleQuestionChange(qIndex, "questionText", e.target.value)} required placeholder="Type your question..." />
                      </div>

                      <div className="form-group" style={{ maxWidth: "200px" }}>
                        <label>Marks Allocation</label>
                        <input type="number" className="form-control" value={q.marks} onChange={(e) => handleQuestionChange(qIndex, "marks", e.target.value)} required placeholder="Ex. 100" />
                      </div>

                      {assessmentType === "mcq" && (
                        <>
                          <div className="form-group">
                            <label>MCQ Options</label>
                            {q.options?.map((opt, oIndex) => (
                              <input 
                                key={oIndex} 
                                type="text" 
                                className="form-control" 
                                style={{ marginBottom: "0.5rem" }} 
                                placeholder={`Option ${oIndex + 1}`} 
                                value={opt} 
                                onChange={(e) => handleOptionChange(qIndex, oIndex, e.target.value)} 
                                required 
                              />
                            ))}
                          </div>
                          
                          {gradingType === "auto" && (
                            <div className="form-group" style={{ maxWidth: "250px" }}>
                              <label>Correct Option Number</label>
                              <select className="form-control" value={q.correctOptionNum} onChange={(e) => handleQuestionChange(qIndex, "correctOptionNum", e.target.value)}>
                                <option value="1">Option 1</option>
                                <option value="2">Option 2</option>
                                <option value="3">Option 3</option>
                                <option value="4">Option 4</option>
                              </select>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}

                  {assessmentType !== "file" && (
                    <button type="button" className="navy-btn" onClick={addQuestion} style={{ display: "block", width: "100%", justifyContent: "center", marginBottom: "2rem", border: "2px dashed var(--navy-primary)", background: "none", color: "var(--navy-primary)" }}>
                      + Add Question
                    </button>
                  )}
                </div>

                <button type="submit" className="navy-btn" style={{ width: "100%", justifyContent: "center" }}>Publish Assessment</button>
              </form>
            </div>
          )}

          {subTab === "deployed-assessments" && (
            <div className="card">
              <h3 className="card-title">Published Assessments</h3>
              <div className="table-container">
                <table className="custom-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Course & Batch</th>
                      <th>Config Type</th>
                      <th>Deadline</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const myExams = assessments.filter(a => (userData.assignedCourses || []).includes(a.courseId));
                      const sortedExams = [...myExams].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

                      return sortedExams.map(exam => (
                        <tr key={exam.id}>
                          <td><strong>{exam.title}</strong><br/><small style={{ color: "var(--text-muted)" }}>Total Questions: {exam.questions ? exam.questions.length : 1}</small></td>
                          <td>{exam.courseId} ({exam.batchName})</td>
                          <td>
                            <span className="badge badge-approved" style={{ background: "#cbd5e1", color: "#1e293b", fontWeight: "bold" }}>
                              {exam.assessmentType?.toUpperCase()} ({exam.gradingType?.toUpperCase()})
                            </span>
                          </td>
                          <td>{new Date(exam.deadline).toLocaleString()}</td>
                          <td>
                            <button className="navy-btn navy-btn-danger" style={{ padding: "0.4rem" }} onClick={() => handleDeleteAssessment(exam.id)}>
                              <Trash2 size={12}/>
                            </button>
                          </td>
                        </tr>
                      ));
                    })()}
                    {assessments.filter(a => (userData.assignedCourses || []).includes(a.courseId)).length === 0 && (
                      <tr>
                        <td colSpan="5" style={{ textAlign: "center", color: "var(--text-muted)", padding: "1.5rem" }}>No assessments published yet.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      )}

      {/* =============================
           GRADING & EVALUATION SYSTEM
          ============================= */}
      {activeTab === "grading" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
          
          {subTab === "new-grading" && (
            <div className="card" style={{ borderTop: "4px solid var(--warning)" }}>
              <h3 className="card-title">New Grading Queue</h3>
              <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", marginBottom: "1.5rem" }}>
                This list shows all student exam submissions awaiting your evaluation.
              </p>
              <div className="table-container">
                <table className="custom-table">
                  <thead>
                    <tr>
                      <th>Student Name</th>
                      <th>Assessment Topic</th>
                      <th>Type of Assessment</th>
                      <th>Exam Date & Time</th>
                      <th>Student Submitted Date</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const pendingSubmissions = mySubmissions
                        .filter(sub => sub.status === "pending")
                        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

                      return pendingSubmissions.map(sub => {
                        const targetExam = assessments.find(a => a.id === sub.assessmentId);
                        return (
                          <tr key={sub.id}>
                            <td><strong>{sub.studentName}</strong></td>
                            <td>{sub.assessmentTitle || "Structured Task"}</td>
                            <td>
                              <span className="badge badge-approved" style={{ background: "#cbd5e1", color: "#1e293b", fontSize: "0.85rem", fontWeight: "bold" }}>
                                {targetExam ? targetExam.assessmentType?.toUpperCase() : "STUDENT TASK"}
                              </span>
                            </td>
                            <td>{targetExam?.createdAt ? new Date(targetExam.createdAt).toLocaleString() : "N/A"}</td>
                            <td>{sub.timestamp ? new Date(sub.timestamp).toLocaleString() : "N/A"}</td>
                            <td><span className="badge badge-pending">{sub.status}</span></td>
                            <td>
                              <button className="navy-btn" onClick={() => setSelectedSubmissionId(sub.id)}>Grade Assessment</button>
                            </td>
                          </tr>
                        );
                      });
                    })()}
                    {mySubmissions.filter(sub => sub.status === "pending").length === 0 && (
                      <tr>
                        <td colSpan="7" style={{ textAlign: "center", color: "var(--text-muted)", padding: "1.5rem" }}>No pending submissions.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {subTab === "already-graded" && (
            <div className="card" style={{ borderTop: "4px solid var(--success)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem", flexWrap: "wrap", gap: "1rem" }}>
                <div>
                  <h3 className="card-title" style={{ margin: 0 }}>Graded History</h3>
                  <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", marginTop: "0.25rem" }}>
                    Archive of all student works evaluated by you.
                  </p>
                </div>
                <button 
                  className="navy-btn" 
                  onClick={handleExportGradedPDF}
                  style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", background: "var(--navy-primary)" }}
                >
                  <Download size={16} /> Download Graded History
                </button>
              </div>

              <div className="table-container">
                <table className="custom-table">
                  <thead>
                    <tr>
                      <th>Student Name</th>
                      <th>Assessment Topic</th>
                      <th>Type of Assessment</th>
                      <th>Student Submitted Date</th>
                      <th>Score Assigned</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const gradedSubmissions = mySubmissions
                        .filter(sub => sub.status === "graded")
                        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

                      return gradedSubmissions.map(sub => {
                        const targetExam = assessments.find(a => a.id === sub.assessmentId);
                        return (
                          <tr key={sub.id}>
                            <td><strong>{sub.studentName}</strong></td>
                            <td>{sub.assessmentTitle || "Structured Task"}</td>
                            <td>
                              <span className="badge badge-approved" style={{ background: "#cbd5e1", color: "#1e293b", fontSize: "0.85rem", fontWeight: "bold" }}>
                                {targetExam ? targetExam.assessmentType?.toUpperCase() : "STUDENT TASK"}
                              </span>
                            </td>
                            <td>{sub.timestamp ? new Date(sub.timestamp).toLocaleString() : "N/A"}</td>
                            <td><strong>{sub.score}/100</strong></td>
                            <td><span className="badge badge-approved">Graded</span></td>
                          </tr>
                        );
                      });
                    })()}
                    {mySubmissions.filter(sub => sub.status === "graded").length === 0 && (
                      <tr>
                        <td colSpan="6" style={{ textAlign: "center", color: "var(--text-muted)", padding: "1.5rem" }}>No graded submissions in history.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {selectedSubmissionId !== null && (
            <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(15,23,42,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 999, padding: "1.5rem" }}>
              <div className="card" style={{ maxWidth: "650px", width: "100%", padding: "2rem", position: "relative", borderTop: "4px solid var(--success)", maxHeight: "90vh", overflowY: "auto" }}>
                
                <button 
                  onClick={() => { setSelectedSubmissionId(null); setGradeScore(""); setGradeFeedback(""); }} 
                  style={{ position: "absolute", top: "1.2rem", right: "1.2rem", background: "none", border: "none", color: "var(--danger)", cursor: "pointer" }}
                >
                  <X size={20} />
                </button>

                <h3 className="card-title" style={{ marginBottom: "1.5rem", fontSize: "1.2rem", fontWeight: "600" }}>Grade Assessment</h3>
                
                <form onSubmit={handleGradeSubmission}>           
                  {(() => {
                    const subObj = submissions.find(s => s.id === selectedSubmissionId);
                    const targetExam = assessments.find(a => a.id === subObj?.assessmentId);
                    const isFileExam = (targetExam?.assessmentType === "file") || (subObj?.studentAnswer?.startsWith("http"));

                    if (!isFileExam) {
                      const examObj = assessments.find(a => a.id === subObj.assessmentId);
                      let parsedAnswers = {};
                      try {
                        parsedAnswers = JSON.parse(subObj.studentAnswer);
                      } catch (e) {
                        parsedAnswers = {};
                      }

                      return (
                        <div style={{ marginBottom: "1.5rem" }}>
                          <h4 style={{ marginBottom: "1rem", color: "var(--navy-primary)", fontSize: "1rem", fontWeight: "600" }}>Evaluation Sheet</h4>
                          {examObj?.questions?.map((q, idx) => {
                            const isMCQ = examObj.assessmentType === "mcq";
                            const maxQMarks = parseFloat(q.marks || 0);

                            return (
                              <div key={idx} className="card" style={{ background: "var(--bg-slate)", marginBottom: "1rem", border: "1px solid #cbd5e1" }}>
                                <p><strong>Q{idx + 1}: {q.questionText}</strong></p>
                                <p style={{ color: "var(--navy-light)", marginTop: "0.25rem", marginBottom: "0.5rem" }}>Student's Answer: <strong>{parsedAnswers[idx] || "Unanswered"}</strong></p>
                                
                                <div className="form-group" style={{ maxWidth: "200px" }}>
                                  <label>Mark Question</label>
                                  <select 
                                    className="form-control" 
                                    value={manualGradeAnswers[idx] || ""} 
                                    onChange={(e) => setManualGradeAnswers({ ...manualGradeAnswers, [idx]: e.target.value })}
                                    required
                                  >
                                    <option value="">Select status</option>
                                    <option value="Correct">Correct</option>
                                    <option value="Wrong">Wrong</option>
                                  </select>
                                </div>

                                {!isMCQ && manualGradeAnswers[idx] === "Correct" && (
                                  <div className="form-group" style={{ maxWidth: "250px" }}>
                                    <label>Allocated Score (editable)</label>
                                    <input 
                                      type="number" 
                                      className="form-control" 
                                      value={structuredOverrideScores[idx] !== undefined ? structuredOverrideScores[idx] : maxQMarks}
                                      onChange={(e) => setStructuredOverrideScores({ ...structuredOverrideScores, [idx]: e.target.value })}
                                      max={maxQMarks}
                                      min="0"
                                    />
                                    <small style={{ color: "var(--text-muted)" }}>Max Marks: {maxQMarks}</small>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    } else {
                      const maxProjectMarks = targetExam?.totalMarks || 100;
                      return (
                        <div style={{ marginBottom: "1.5rem" }}>
                          <div style={{ backgroundColor: "#f1f5f9", padding: "1.5rem", borderRadius: "10px", marginBottom: "1.5rem", borderLeft: "5px solid var(--navy-primary)" }}>
                            <h4 style={{ marginBottom: "0.5rem", color: "var(--navy-primary)", fontSize: "1rem" }}>Submitted Project File</h4>
                            <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", marginBottom: "1rem" }}>
                              Please download and evaluate the student's uploaded project files before committing marks:
                            </p>
                            <a 
                              href={subObj.studentAnswer} 
                              target="_blank" 
                              rel="noreferrer" 
                              className="navy-btn" 
                              style={{ padding: "0.6rem 1.2rem", fontSize: "0.85rem", textDecoration: "none", display: "inline-block" }}
                            >
                              Download Submitted Project File
                            </a>
                          </div>
                          <div className="form-group">
                            <label>Award Score (0 - {maxProjectMarks})</label>
                            <input 
                              type="number" 
                              className="form-control" 
                              value={gradeScore} 
                              onChange={(e) => setGradeScore(e.target.value)} 
                              required 
                              min="0" 
                              max={maxProjectMarks}
                            />
                          </div>
                          <small style={{ color: "var(--text-muted)", display: "block", marginTop: "0.25rem" }}>Max Marks: {maxProjectMarks}</small>
                        </div>
                      );
                    }
                  })()}

                  <div className="form-group">
                    <label>Feedback (Optional)</label>
                    <textarea className="form-control" value={gradeFeedback} onChange={(e) => setGradeFeedback(e.target.value)} placeholder="Provide feedback here" required />
                  </div>
                  
                  <div style={{ display: "flex", gap: "0.5rem", marginTop: "1.5rem" }}>
                    <button type="submit" className="navy-btn" style={{ background: "var(--success)" }}>Commit Grades</button>
                    <button type="button" className="navy-btn navy-btn-danger" onClick={() => { setSelectedSubmissionId(null); setGradeScore(""); setGradeFeedback(""); }}>Cancel</button>
                  </div>
                </form>

              </div>
            </div>
          )}

        </div>
      )}

      {/* Announcements View Tab */}
      {activeTab === "announcements" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
          
          {subTab === "broadcast" && (
            <div className="card" style={{ borderTop: "4px solid var(--navy-primary)" }}>
              <h3 className="card-title">Announcements</h3>
              <form onSubmit={handlePublishClassAnnouncement}>
                <div className="form-group">
                  <label>Select Course</label>
                  <select className="form-control" value={announceCourse} onChange={(e) => setAnnounceCourse(e.target.value)} required>
                    <option value="">Select Course</option>
                    {assignedCourses.map(c => (
                      <option key={c.id} value={c.code}>{c.name} ({c.code})</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Select Batch</label>
                  <select className="form-control" value={announceBatch} onChange={(e) => setAnnounceBatch(e.target.value)} required>
                    <option value="">Select Batch</option>
                    {assignedBatches.filter(b => b.courseId === courses.find(c => c.code === announceCourse)?.id).map(b => (
                      <option key={b.id} value={b.batchName}>{b.batchName}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Announcement Title</label>
                  <input type="text" className="form-control" value={announceTitle} onChange={(e) => setAnnounceTitle(e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>Announcement Message</label>
                  <textarea className="form-control" style={{ minHeight: "120px" }} value={announceContent} onChange={(e) => setAnnounceContent(e.target.value)} required />
                </div>
                <button type="submit" className="navy-btn">Send Announcement</button>
              </form>
            </div>
          )}

          {subTab === "sent-history" && (
            <div className="card">
              <h3 className="card-title">Announcements History</h3>
              <div className="table-container">
                <table className="custom-table">
                  <thead>
                    <tr>
                      <th>Title & Content Message</th>
                      <th>Target Course Batch</th>
                      <th>Broadcast Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {announcementsList.filter(ann => ann.publishedBy === userData.fullName).map(ann => (
                      <tr key={ann.id}>
                        <td><strong>{ann.title}</strong><br/><small style={{ color: "var(--text-muted)" }}>{ann.content}</small></td>
                        <td><span className="badge badge-approved" style={{ background: "#cbd5e1", color: "#1e293b" }}>{ann.targetCourseId} ({ann.targetBatchId})</span></td>
                        <td>{new Date(ann.timestamp || ann.date).toLocaleString()}</td>
                        <td>
                          <div style={{ display: "flex", gap: "0.5rem" }}>
                            <button className="navy-btn" style={{ padding: "0.4rem", background: "var(--navy-light)" }} onClick={() => handleEditAnnouncement(ann)}><Edit size={12}/></button>
                            <button className="navy-btn navy-btn-danger" style={{ padding: "0.4rem" }} onClick={() => handleDeleteAnnouncement(ann.id)}><Trash2 size={12}/></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {announcementsList.filter(ann => ann.publishedBy === userData.fullName).length === 0 && (
                      <tr>
                        <td colSpan="4" style={{ textAlign: "center", color: "var(--text-muted)", padding: "1.5rem" }}>No class announcements sent by you yet.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {subTab === "received" && (
            <div className="card" style={{ borderTop: "4px solid var(--success)" }}>
              <h3 className="card-title">My Received Announcements</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem", marginTop: "1rem" }}>
                {(() => {
                  const myReceived = announcementsList.filter(ann => ann.targetType === "all" || ann.targetRole === "instructor");
                  const sortedReceived = [...myReceived].sort((a, b) => new Date(b.timestamp || b.date).getTime() - new Date(a.timestamp || a.date).getTime());
                  const persistentReads = userData.readNotifications || [];

                  return sortedReceived.map(ann => {
                    const isRead = persistentReads.includes(ann.id);
                    return (
                      <div 
                        key={ann.id} 
                        onClick={() => markAsRead(ann.id)}
                        style={{ 
                          background: isRead ? "var(--white)" : "#f1f5f9", 
                          borderLeft: isRead ? "5px solid #cbd5e1" : "5px solid var(--success)",
                          padding: "1.5rem",
                          borderRadius: "8px",
                          position: "relative",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                          cursor: "pointer"
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.75rem" }}>
                          <h4 style={{ color: "var(--navy-primary)", margin: 0 }}>{ann.title}</h4>                        
                        </div>
                        <p style={{ lineHeight: "1.6", color: "var(--text-main)" }}>{ann.content}</p>
                        <small style={{ color: "var(--text-muted)", display: "block", marginTop: "0.75rem" }}>
                          Published: {new Date(ann.timestamp || ann.date).toLocaleString()}
                        </small>
                      </div>
                    );
                  });
                })()}
                {announcementsList.filter(ann => ann.targetType === "all" || ann.targetRole === "instructor").length === 0 && (
                  <p style={{ textAlign: "center", color: "var(--text-muted)", padding: "1.5rem" }}>No Announcements received yet.</p>
                )}
              </div>
            </div>
          )}

          {editingAnnounceId !== null && (
            <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(15,23,42,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 999, padding: "1.5rem" }}>
              <div className="card" style={{ maxWidth: "550px", width: "100%", padding: "2rem", position: "relative", borderTop: "4px solid var(--navy-primary)" }}>
                
                <button 
                  onClick={() => { setEditingAnnounceId(null); setAnnounceTitle(""); setAnnounceContent(""); setAnnounceCourse(""); setAnnounceBatch(""); }} 
                  style={{ position: "absolute", top: "1.2rem", right: "1.2rem", background: "none", border: "none", color: "var(--danger)", cursor: "pointer" }}
                >
                  <X size={20} />
                </button>
                
                <h3 className="card-title" style={{ marginBottom: "1.5rem", fontSize: "1.2rem", fontWeight: "600" }}>Modify Announcement</h3>
                
                <form onSubmit={handlePublishClassAnnouncement}>
                  <div className="form-group">
                    <label>Announcement Title</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={announceTitle} 
                      onChange={(e) => setAnnounceTitle(e.target.value)} 
                      required 
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Announcement Message</label>
                    <textarea 
                      className="form-control" 
                      style={{ minHeight: "120px" }} 
                      value={announceContent} 
                      onChange={(e) => setAnnounceContent(e.target.value)} 
                      required 
                    />
                  </div>
                  
                  <div style={{ display: "flex", gap: "0.5rem", marginTop: "1.5rem" }}>
                    <button type="submit" className="navy-btn">Update Announcement</button>
                    <button 
                      type="button" 
                      className="navy-btn navy-btn-danger" 
                      onClick={() => { setEditingAnnounceId(null); setAnnounceTitle(""); setAnnounceContent(""); setAnnounceCourse(""); setAnnounceBatch(""); }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>

              </div>
            </div>
          )}

        </div>
      )}

    </div>
  );
}